import { useState, type FormEvent } from 'react';
import { siteInfo } from '@/mocks/home';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/dahb83jp14a1h3mr6hhg';

export default function Subscribe() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setFormMessage('订阅成功，瓜田日报会按周送到你的邮箱。');
      return;
    }

    const email = String(formData.get('email') ?? '').trim();
    if (!email) {
      setStatus('error');
      setFormMessage('请先填写邮箱地址。');
      return;
    }

    setStatus('loading');
    setFormMessage('');

    const body = new URLSearchParams();
    body.append('email', email);
    body.append('source', '瓜田吃瓜首页订阅');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const code = parsed?.code ?? '';
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';

      const isSpam = /spam/i.test(serverMsg);
      const ok = response.ok && code === 'OK' && !isSpam;

      if (ok) {
        setStatus('success');
        setFormMessage('订阅成功，瓜田日报会按周送到你的邮箱。');
        form.reset();
      } else {
        setStatus('error');
        setFormMessage(serverMsg || '提交失败，请稍后再试。');
      }
    } catch {
      setStatus('error');
      setFormMessage('网络有点问题，请稍后重试。');
    }
  };

  return (
    <section id="subscribe" className="w-full bg-secondary-100 py-14 md:py-20">
      <div className="w-full px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          <div>
            <span className="inline-flex items-center gap-2 text-xs md:text-sm px-3 py-1 rounded-full bg-background-50 text-secondary-900">
              <i className="ri-mail-send-line" />
              订阅瓜田日报
            </span>
            <h2 className="font-heading text-2xl md:text-4xl text-foreground-950 mt-3 md:mt-4">
              每周一封，只送最好吃的几口瓜
            </h2>
            <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-700">
              我们会把你可能错过的冷知识、本周最好笑的段子和新上架的精选表情包整理成一封短邮件。
              不啰嗦，随时可以退订。
            </p>

            <form
              id="guitian-newsletter-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="mt-6 flex flex-col gap-3"
            >
              <label htmlFor="newsletter-email" className="text-sm text-foreground-700">
                你的邮箱
              </label>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  id="newsletter-email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="you@example.com"
                  className="flex-1 px-4 py-3 rounded-md bg-background-50 border border-secondary-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 disabled:opacity-60 transition-colors whitespace-nowrap"
                >
                  <i className={status === 'loading' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'} />
                  {status === 'loading' ? '提交中' : '立即订阅'}
                </button>
              </div>
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="field-check-extra"
              />
              {formMessage && (
                <p
                  className={`text-sm ${status === 'success' ? 'text-primary-700' : 'text-accent-700'}`}
                  role="status"
                >
                  {formMessage}
                </p>
              )}
            </form>
          </div>

          <div id="contact" className="rounded-lg bg-background-50 border border-secondary-200 p-6 md:p-8 self-start">
            <h3 className="font-heading text-lg md:text-xl text-foreground-950">联系与投稿</h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              有想分享的瓜田故事、段子，或者想建议我们聊聊某个围观现象？随时写信过来，我们都会看。
            </p>
            <ul className="mt-5 space-y-3 text-sm">
              <li className="flex items-center gap-3 text-foreground-700">
                <span className="w-9 h-9 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center">
                  <i className="ri-mail-line" />
                </span>
                <a href={`mailto:${siteInfo.email}`} className="hover:text-primary-700 transition-colors">
                  {siteInfo.email}
                </a>
              </li>
              <li className="flex items-center gap-3 text-foreground-700">
                <span className="w-9 h-9 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center">
                  <i className="ri-edit-2-line" />
                </span>
                投稿请注明是否希望公开署名
              </li>
              <li className="flex items-center gap-3 text-foreground-700">
                <span className="w-9 h-9 rounded-lg bg-secondary-100 text-secondary-800 flex items-center justify-center">
                  <i className="ri-time-line" />
                </span>
                我们通常在三个工作日内回复
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}